"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../shadcn/ui/alert-dialog";
import { ArrowUpTrayIcon, CheckCircleIcon } from "@heroicons/react/20/solid";
import React, { useCallback, useState } from "react";

import { DragAndDropAreaImage } from "./DragAndDropAreaImage";
import Loader from "./icons/Loader";
import { getApiClient } from "@punchup/utils/apiRegistry";

type VenueImageType = "logoImg" | "carouselImg";
type AddonImageType = "addonImg" | "variationImg";

type SpecialImageType =
  | "previewImg"
  | "smallPreviewImg"
  | "posterImageWithTitles";

type ComedianImageType = "aboutImg" | "signatureImg";

export type PunchupImageMetadata = {
  id: string;
  imageType:
    | SpecialImageType
    | ComedianImageType
    | VenueImageType
    | AddonImageType;
};

interface GenerateImageUploadURLResponse {
  oneTimeUploadUrl: string;
  imgId: string;
}

export const uploadImage = async (imageFile: File): Promise<string> => {
  const MAX_IMAGE_SIZE_BYTES = 19 * 1024 * 1024; // 19MB
  if (imageFile.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error("Image is too large. It must be less than 19MB");
  }

  const apiClient = getApiClient();

  const { oneTimeUploadUrl, imgId } =
    await apiClient.post<GenerateImageUploadURLResponse>(
      "/api/generateImageUploadURL",
      {}
    );

  if (!oneTimeUploadUrl) {
    throw new Error("Failed to get the one-time upload URL.");
  }

  const formData = new FormData();
  formData.append("file", imageFile);

  // Upload to external Cloudflare URL
  const uploadResult = await fetch(oneTimeUploadUrl, {
    method: "POST",
    body: formData,
  });

  if (uploadResult.ok) {
    if (imgId) {
      return imgId;
    }
    throw new Error("Error while uploading image: No image ID received");
  } else {
    const errorJson = await uploadResult.json();
    const sizeError = errorJson?.errors?.find(
      (e: { code: number }) => e.code === 5413
    );
    if (sizeError) {
      throw new Error("Image is too large. It must be less than 19MB");
    }
    throw new Error("Error while uploading image");
  }
};

interface Props {
  onChangeUploadedImage: (uploadedImage: PunchupImageMetadata) => void;
  buttonLabel?: string;
  entityLabel?: string;
  imageType: PunchupImageMetadata["imageType"];
  containerClassNames?: string;
  displayImageId?: boolean;
  disabled?: boolean;
  /** Custom trigger element to open the modal. If not provided, uses default button. */
  trigger?: React.ReactNode;
}

export const ImageUploaderModal = ({
  onChangeUploadedImage,
  buttonLabel = "Upload Image",
  entityLabel = "comedian",
  imageType,
  containerClassNames = "mt-5 ml-2",
  displayImageId = true,
  disabled = false,
  trigger,
}: Props) => {
  const emptyImage: PunchupImageMetadata = {
    id: "",
    imageType,
  };

  const [uploadedImage, setUploadedImage] =
    useState<PunchupImageMetadata>(emptyImage);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onReceivedImage = useCallback(
    async (image: File) => {
      setIsUploading(true);
      setErrorMessage(null);
      try {
        if (!uploadedImage.id) {
          const imgId = await uploadImage(image);

          if (imgId) {
            setUploadedImage((prevState) => {
              return {
                ...prevState,
                id: imgId,
              };
            });
          }
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upload image";
        setErrorMessage(message);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadedImage]
  );

  return (
    <AlertDialog>
      <div className={containerClassNames}>
        {trigger ? (
          <AlertDialogTrigger asChild disabled={disabled}>
            {trigger}
          </AlertDialogTrigger>
        ) : (
          <>
            <AlertDialogTrigger
              className="btn flex items-center justify-center text-white p-2 cursor-pointer"
              disabled={disabled}
            >
              <ArrowUpTrayIcon className="w-5" />
              <p className="px-2">{buttonLabel}</p>
            </AlertDialogTrigger>
          </>
        )}
      </div>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Upload Image</AlertDialogTitle>
          <AlertDialogDescription>
            {`Use this form to upload an image for this ${entityLabel}.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!!uploadedImage.id.length ? (
          <>
            <div className="flex gap-x-2 items-center text-green-800">
              <CheckCircleIcon className="w-8" /> The upload was successful!
            </div>
            {displayImageId && (
              <p>
                Received image with id: <strong>{uploadedImage.id}</strong>{" "}
              </p>
            )}
          </>
        ) : (
          <>
            <DragAndDropAreaImage
              onReceivedImage={onReceivedImage}
              containerClassNames="h-32 flex items-center justify-center rounded-xl"
              isUploading={isUploading}
            />
            {errorMessage && (
              <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
            )}
          </>
        )}
        <div className="flex w-full justify-between">
          <AlertDialogCancel
            onClick={() => {
              setUploadedImage(emptyImage);
              setErrorMessage(null);
            }}
          >
            Cancel Upload
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!uploadedImage.id}
            onClick={() => {
              if (uploadedImage.id) {
                onChangeUploadedImage(uploadedImage);
                setUploadedImage(emptyImage);
              }
            }}
          >
            {isUploading ? <Loader /> : "Save"}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
